import React, {
  Suspense, lazy, useCallback, useState, memo,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage, injectIntl } from 'react-intl';
import { compose } from 'lodash/fp';
import { Fade, Modal, Tooltip } from '@material-ui/core';
import { ZoomOutMap, Visibility, VisibilityOff } from '@material-ui/icons';
import { Spinner } from '../../common/components';
import { hexToRgb } from '../../common/utils';
import messages from '../messages';

const ExpandedPDFViewer = lazy(() => import(/* webpackChunkName: 'ExpandedPDFViewer', webpackPrefetch: true */ './ExpandedPDFViewer'));

const S = {
  Wrapper: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 30px;
    padding: 4px 8px;
    border-top: 1px solid ${({ theme }) => theme.palette.grey[300]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    background-color: ${({ theme }) => theme.palette.grey[100]};
  `,
  PageNumber: styled.p`
    position: relative;
    left: 50%;
    transform: translateX(-50%);
    margin-right: auto;
    color: ${({ theme }) => theme.palette.grey[700]};
  `,
  PageValue: styled.data`
    margin-right: 4px;
  `,
  Tooltip: styled(({ classes, className, ...otherProps }) => (
    <Tooltip
      className={className}
      classes={{
        ...classes,
        popper: className,
        tooltip: 'tooltip',
      }}
      {...otherProps}
    />
  ))`
    .tooltip {
      position: relative;
      padding: 6px 16px;
      background-color: ${({ theme }) => theme.palette.grey[700]};
    }
    .tooltip::after {
      content: '';
      position: absolute;
      left: 50%;
      bottom: 0;
      transform: translate(-50%, 100%);
      border: 5px solid transparent;
      border-top: 7px solid ${({ theme }) => theme.palette.grey[700]};
    }
  `,
  Button: styled.button`
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-weight: 500;
    cursor: pointer;
    outline: none;
    :hover {
      text-decoration: underline;
    }
    :not(:last-child) {
      margin-right: 8px;
    }
  `,
  Visibility: styled(Visibility)`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 1.5rem;
  `,
  VisibilityOff: styled(VisibilityOff)`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 1.5rem;
  `,
  ZoomOutMap: styled(ZoomOutMap)`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: 1.5rem;
  `,
  Modal: styled(Modal).attrs(({ BackdropProps }) => ({
    BackdropProps: {
      ...BackdropProps,
      style: {
        backgroundColor: hexToRgb('#000', 0.87),
      },
    },
  }))`
  `,
};

const StatsViewportFooter = React.memo(({
  fileUrl, intl, numPages, pageNumber, survey, visible, toggleQuestionVisibility,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const showExpandedPDFViewport = useCallback(() => setIsExpanded(true), []);
  const hideExpandedPDFViewport = useCallback(() => setIsExpanded(false), []);

  return (
    <>
      <S.Wrapper>
        <S.PageNumber>
          <FormattedMessage
            {...messages.statsViewportFooterPageNumber}
            values={{
              page: (
                <S.PageValue value={pageNumber}>
                  {`${pageNumber}/${numPages}`}
                </S.PageValue>
              ),
            }}
          />
        </S.PageNumber>
        <S.Tooltip
          title={intl.formatMessage(
            messages[`statsViewportFooterTooltip${visible ? 'VisibilityOff' : 'Visibility'}`],
          )}
          placement="top"
        >
          <S.Button type="button" onClick={toggleQuestionVisibility}>
            {visible ? <S.Visibility /> : <S.VisibilityOff />}
          </S.Button>
        </S.Tooltip>
        <S.Tooltip
          title={intl.formatMessage(messages.statsViewportFooterTooltipZoomOutMap)}
          placement="top"
        >
          <S.Button type="button" onClick={showExpandedPDFViewport}>
            <S.ZoomOutMap />
          </S.Button>
        </S.Tooltip>
      </S.Wrapper>
      <S.Modal
        open={isExpanded}
        onClose={hideExpandedPDFViewport}
      >
        <Fade in={isExpanded}>
          <Suspense fallback={<Spinner />}>
            <ExpandedPDFViewer fileUrl={fileUrl} survey={survey} onClose={hideExpandedPDFViewport} />
          </Suspense>
        </Fade>
      </S.Modal>
    </>
  );
});

StatsViewportFooter.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  fileUrl: PropTypes.string.isRequired,
  numPages: PropTypes.number,
  pageNumber: PropTypes.number,
  survey: PropTypes.shape({
    data: PropTypes.shape({}),
  }).isRequired,
  visible: PropTypes.bool.isRequired,
  toggleQuestionVisibility: PropTypes.func.isRequired,
};

StatsViewportFooter.defaultProps = {
  numPages: 0,
  pageNumber: 1,
};

export default compose(
  memo,
  injectIntl,
)(StatsViewportFooter);
